import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import api from "@/lib/api";
import { money, dateFmt } from "@/lib/format";

const TYPES = {
  roi: { l: "ROI (USDT)", c: "emerald" }, level: { l: "Level", c: "sky" },
  matching: { l: "Matching", c: "amber" }, salary: { l: "Salary", c: "purple" },
  royalty: { l: "Royalty", c: "rose" }, turnover: { l: "Turnover", c: "cyan" },
  points: { l: "ROI Points", c: "amber" },
};

export default function Income() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/me/incomes").then(({ data }) => setRows(data)).catch(() => toast.error("Load failed")); }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.type === filter);
  const total = filtered.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="space-y-6" data-testid="income-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Coins className="w-6 h-6 text-amber-400" /> Income History</h1>
        <div className="glass rounded-xl px-5 py-3">
          <span className="text-xs text-slate-400 uppercase tracking-wide">Filtered Total</span>
          <div className="font-mono-fin text-xl font-bold text-emerald-400">{money(total)}</div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter("all")} className={`px-4 py-2 rounded-lg text-sm ${filter === "all" ? "gold-btn font-bold" : "border border-amber-500/20 text-slate-400"}`}>All</button>
        {Object.entries(TYPES).map(([k, v]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-lg text-sm ${filter === k ? "gold-btn font-bold" : "border border-amber-500/20 text-slate-400"}`}>{v.l}</button>
        ))}
      </div>

      <div className="glass rounded-2xl p-6">
        {filtered.length === 0 ? (
          <p className="text-slate-500 text-sm">No income records yet. Your earnings will appear here as they accrue.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                <th className="text-left py-3">Type</th><th className="text-left py-3">Description</th><th className="text-right py-3">Amount</th><th className="text-right py-3">Date</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-800/60" data-testid={`income-row-${r.id}`}>
                    <td className="py-3"><span className={`text-xs px-2 py-1 rounded bg-${(TYPES[r.type]?.c || "slate")}-500/15 text-${(TYPES[r.type]?.c || "slate")}-400`}>{TYPES[r.type]?.l || r.type}</span></td>
                    <td className="py-3 text-slate-300">{r.description}</td>
                    <td className="py-3 text-right font-mono-fin text-emerald-400 font-bold">{money(r.amount)}</td>
                    <td className="py-3 text-right text-slate-400 text-xs">{dateFmt(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
